import api from "../axios";

const getAccountId = () => {
  return sessionStorage.getItem("acc_id");
};

export const getCaseManagers = async (for_assignment=false) => {
  const accountId = getAccountId();
  const response = await api.get(`/accounts/${accountId}/case-managers/`,
    {
      params: {
        "for_assignment": for_assignment,
      },
    }
  );
  return response.data;
};

export const getStaff = async (locationId) => {
  const accountId = getAccountId();
  const response = await api.get(
    `/accounts/${accountId}/${locationId}/staff-list/`,
    {
      params: { status: "active" },
    }
  );
  return response.data;
};

export const getRpmCodes = async () => {
  const accountId = getAccountId();
  const response = await api.get(`/rpm/${accountId}/rpm-codes/`);
  return response.data;
};

export const getCcmCodes = async (locationId) => {
  const accountId = getAccountId();
  const response = await api.get(
    `/conditions/${accountId}/${locationId}/chronic-conditions/`
  );
  return response.data;
};

export const getLanguages = async () => {
  const accountId = getAccountId();
  //   const response = await api.get(`/languages/${accountId}/languages/`);
  const response = {
    data: [
      { id: 1, name: "English" },
      { id: 2, name: "Spanish" },
      { id: 3, name: "French" },
      { id: 4, name: "Farsi" },
      { id: 5, name: "Armenian" },
      { id: 6, name: "Arabic" },
      { id: 7, name: "Vietnamese" },
      { id: 8, name: "Chinese" },
      { id: 9, name: "Hindi" },
      { id: 10, name: "Tagalog" },
      { id: 11, name: "Russian" },
      { id: 12, name: "Korean" },
    ],
  };

  return response.data;
};

export const getStates = async () => {
  const accountId = getAccountId();
  //   const response = await api.get(`/accounts/${accountId}/states/`);
  const states = [
    "Alabama",
    "Alaska",
    "Arizona",
    "Arkansas",
    "California",
    "Colorado",
    "Connecticut",
    "Delaware",
    "Florida",
    "Georgia",
    "Hawaii",
    "Idaho",
    "Illinois",
    "Indiana",
    "Iowa",
    "Kansas",
    "Kentucky",
    "Louisiana",
    "Maine",
    "Maryland",
    "Massachusetts",
    "Michigan",
    "Minnesota",
    "Mississippi",
    "Missouri",
    "Montana",
    "Nebraska",
    "Nevada",
    "New Hampshire",
    "New Jersey",
    "New Mexico",
    "New York",
    "North Carolina",
    "North Dakota",
    "Ohio",
    "Oklahoma",
    "Oregon",
    "Pennsylvania",
    "Rhode Island",
    "South Carolina",
    "South Dakota",
    "Tennessee",
    "Texas",
    "Utah",
    "Vermont",
    "Virginia",
    "Washington",
    "West Virginia",
    "Wisconsin",
    "Wyoming",
  ];

  const response = {
    data: states.map((state, index) => ({ id: index + 1, name: state })),
  };
  return response.data;
};

export const getProviders = async () => {
  const accountId = getAccountId();
  const response = await api.get(`/accounts/${accountId}/providers/`);
  return response.data;
};

/**
 * Dashboard patient list. The endpoint moved pagination, search, chips, status
 * filters, advance filters and column sorts server-side, so every filter is now
 * a query param (see PATIENT_LIST_API.md). The dashboard walks the pages
 * silently in the background, so this just builds the params for one page.
 *
 * @param {{
 *   locationId?: string, staffId?: string, isCaseManager?: boolean, isInactive?: boolean,
 *   page?: number, pageSize?: number,
 *   search?: string, name?: string, language?: string,
 *   chip?: "out_of_range"|"monitoring_session"|"unseen_readings"|"reading_reminder",
 *   status?: (string|number)[]|string, caseManager?: string|number, provider?: string|number,
 *   type?: "rpm"|"ccm", rpm?: boolean, ccm?: boolean, compliant?: boolean,
 *   minReadingDays?: number, maxReadingDays?: number,
 *   minRpmMinutes?: number, maxRpmMinutes?: number,
 *   minCcmMinutes?: number, maxCcmMinutes?: number,
 *   sort?: "name"|"time_rpm"|"time_ccm"|"latest_call", order?: "asc"|"desc",
 * }} options
 */
export const getDashboardPatients = async ({
  locationId,
  staffId,
  isCaseManager,
  isInactive,
  page,
  pageSize,
  search,
  name,
  language,
  chip,
  status,
  caseManager,
  provider,
  type,
  rpm,
  ccm,
  compliant,
  minReadingDays,
  maxReadingDays,
  minRpmMinutes,
  maxRpmMinutes,
  minCcmMinutes,
  maxCcmMinutes,
  sort,
  order,
} = {}) => {
  const accountId = getAccountId();

  const url = isCaseManager
    ? `/patients/${accountId}/${staffId}/staff-patients/`
    : `/patients/${accountId}/${locationId}/patients/`;

  const params = {};
  if (isInactive) params.only_active = false;
  if (page) params.page = page;
  if (pageSize) params.page_size = pageSize;
  if (search) params.search = search;
  if (name) params.name = name;
  if (language) params.language = language;
  if (chip) params.chip = chip;
  if (status != null && status !== "" && (!Array.isArray(status) || status.length))
    params.status = Array.isArray(status) ? status.join(",") : status;
  if (caseManager) params.case_manager = caseManager;
  if (provider) params.provider = provider;
  if (type) params.type = type;
  if (typeof rpm === "boolean") params.rpm = rpm;
  if (typeof ccm === "boolean") params.ccm = ccm;
  if (typeof compliant === "boolean") params.compliant = compliant;
  if (minReadingDays != null) params.min_reading_days = minReadingDays;
  if (maxReadingDays != null) params.max_reading_days = maxReadingDays;
  if (minRpmMinutes != null) params.min_rpm_minutes = minRpmMinutes;
  if (maxRpmMinutes != null) params.max_rpm_minutes = maxRpmMinutes;
  if (minCcmMinutes != null) params.min_ccm_minutes = minCcmMinutes;
  if (maxCcmMinutes != null) params.max_ccm_minutes = maxCcmMinutes;
  if (sort) params.sort = sort;
  if (order) params.order = order;

  const response = await api.get(url, { params });
  return response.data;
};

export const getCaseManagerPatientStats = async (staffId) => {
  const accountId = getAccountId();
  const locationId = JSON.parse(sessionStorage.getItem("current_loc"))?.id;
  const response = await api.get(
    `/accounts/${accountId}/case-managers/${staffId}/patient-stats/`,
    {
      params: {
        location: locationId,
      },
    }
  );
  return response.data;
};
